import { useNavigate } from "react-router-dom";
import { useTranslations } from "@/lib/useTranslations";
import { startTransition, useActionState, useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { isEventContentDirty } from "@/lib/content-dirty";
import { getLibraryReturnHref } from "@/lib/library-search-snapshot";

import {
  deleteEventSignalAction,
  updateEventAssignment,
  updateEventContent,
  updateEventCoverImage,
  updateEventInternalNotes,
  updateEventPlace,
  updateEventStatus,
  uploadEventCoverImage,
} from "@/api/events";
import { Button } from "@/components/ui/Button";
import { FieldLabel } from "@/components/ui/FieldLabel";
import { ArticleRichTextEditor } from "@/components/ui/admin/articles/ArticleRichTextEditor";
import { EventActivityFields } from "@/components/ui/admin/events/EventActivityFields";
import { EventAssigneeField } from "@/components/ui/admin/events/EventAssigneeField";
import { EventCoverImageField } from "@/components/ui/admin/events/EventCoverImageField";
import { EventEditorialCycleCard } from "@/components/ui/admin/events/EventEditorialCycleCard";
import { EventEditorialChecklist } from "@/components/ui/admin/events/EventEditorialChecklist";
import { EventPlaceField } from "@/components/ui/admin/events/EventPlaceField";
import { EventTaxonomyPanel } from "@/components/ui/admin/events/EventTaxonomyPanel";
import { InternalNotesField } from "@/components/ui/admin/InternalNotesField";
import { ShareLinkButton } from "@/components/ui/admin/ShareLinkButton";
import type {
  ActivitySchedule,
  ActivityTypeValue,
  EventAssigneeOption,
  EventCategoryValue,
  EventEditStatusValue,
  EventEditor,
  EventTaxonomy,
  UpdateEventState,
} from "@/core/services/types/event.types";
import {
  eventCalendarDayToEndIso,
  eventCalendarDayToStartIso,
  eventIsoToCalendarDay,
} from "@/lib/event-dates";
import {
  emptyPonctuelSchedule,
  emptyRecurrentSchedule,
  EVENT_CATEGORY_VALUES,
  isEventEditorialChecklistComplete,
  parseActivityScheduleFromDb,
  resolveEventEditStatus,
} from "@/core/services/types/event.types";
import type { PlaceSelectOption } from "@/core/services/types/place.types";
import type { ArtForm, ArtisticPeriod, CulturalArea, ExhibitionType, TaxonomyData } from "@/core/services/types/taxonomy.types";

const initialUpdateEventState: UpdateEventState = { success: false };

type EventEditQueryData = {
  event: EventEditor;
  teamMembers: EventAssigneeOption[];
  selectedPlace: PlaceSelectOption | null;
  eventTaxonomy: EventTaxonomy;
  taxonomyData: TaxonomyData;
};

const titleInputClassName =
  "w-full wireframe-border bg-whart-bg px-4 py-3 text-3xl font-bold text-whart-text outline-none placeholder:text-neutral-300 focus:border-whart-dark disabled:opacity-50";

const textareaClassName =
  "w-full resize-y wireframe-border bg-whart-bg px-4 py-3 text-sm leading-relaxed text-whart-text outline-none focus:border-whart-dark disabled:opacity-50";

const dateInputClassName =
  "w-full wireframe-border bg-whart-bg px-3 py-2 text-sm text-whart-text outline-none focus:border-whart-dark disabled:opacity-50";

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="text-xs text-whart-urgent">{message}</p>;
}

function SaveSpinner() {
  return (
    <span
      aria-hidden
      className="inline-block size-3.5 animate-spin rounded-full border-2 border-zinc-900 border-r-transparent"
    />
  );
}

interface EventEditFormProps {
  event: EventEditor;
  assignableMembers: EventAssigneeOption[];
  selectedPlace: PlaceSelectOption | null;
  taxonomy: EventTaxonomy;
  artForms: ArtForm[];
  artisticPeriods: ArtisticPeriod[];
  culturalAreas: CulturalArea[];
  exhibitionTypes: ExhibitionType[];
  shareUrl: string;
}

export function EventEditForm({
  event,
  assignableMembers,
  selectedPlace,
  taxonomy,
  artForms,
  artisticPeriods,
  culturalAreas,
  exhibitionTypes,
  shareUrl,
}: EventEditFormProps) {
  const t = useTranslations("events.edit");
  const tCommon = useTranslations("common");
  const tEventCategories = useTranslations("events.edit.eventCategories");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAdmin } = useAuth();
  const [contentState, contentFormAction, isContentPending] = useActionState(
    updateEventContent,
    initialUpdateEventState,
  );
  const [statusState, statusFormAction, isStatusPending] = useActionState(
    updateEventStatus,
    initialUpdateEventState,
  );
  const [assignmentState, assignmentAction, isAssignmentPending] =
    useActionState(updateEventAssignment, initialUpdateEventState);
  const [coverImageState, coverImageAction, isCoverImagePending] =
    useActionState(updateEventCoverImage, initialUpdateEventState);
  const [coverImageUploadState, coverImageUploadAction, isCoverImageUploadPending] =
    useActionState(uploadEventCoverImage, initialUpdateEventState);
  const [placeState, placeAction, isPlacePending] = useActionState(
    updateEventPlace,
    initialUpdateEventState,
  );
  const [internalNotesState, internalNotesAction, isInternalNotesPending] =
    useActionState(updateEventInternalNotes, initialUpdateEventState);
  const [assignedTo, setAssignedTo] = useState(event.assigned_to ?? "");
  const [placeId, setPlaceId] = useState(event.place_id ?? "");
  const [currentPlace, setCurrentPlace] = useState(selectedPlace);

  useEffect(() => {
    setCurrentPlace(selectedPlace);
  }, [selectedPlace]);
  const [pendingStatusTarget, setPendingStatusTarget] =
    useState<EventEditStatusValue | null>(null);
  const [title, setTitle] = useState(event.title);
  const [descriptionShort, setDescriptionShort] = useState(
    event.description_short ?? "",
  );
  const [descriptionLong, setDescriptionLong] = useState(
    event.description_long ?? "",
  );
  const [whartReviewTitle, setWhartReviewTitle] = useState(
    event.whart_review_title ?? "",
  );
  const [whartReviewContent, setWhartReviewContent] = useState(
    event.whart_review_content ?? "",
  );
  const [whartElse, setWhartElse] = useState(event.whart_else ?? "");
  const [startDateLocal, setStartDateLocal] = useState(
    eventIsoToCalendarDay(event.start_date),
  );
  const [endDateLocal, setEndDateLocal] = useState(
    eventIsoToCalendarDay(event.end_date),
  );
  const [eventCategory, setEventCategory] = useState(
    event.event_category ?? "TEMPORARY_COLLECTION",
  );
  const [activityType, setActivityType] = useState<ActivityTypeValue | "">(
    event.activity_type ?? "",
  );
  const [isFamily, setIsFamily] = useState(event.is_family ?? false);
  const [isRecurrent, setIsRecurrent] = useState(event.is_recurrent ?? false);
  const [pricingInfo, setPricingInfo] = useState(event.pricing_info ?? "");
  const [activitySchedule, setActivitySchedule] = useState<ActivitySchedule>(
    () =>
      parseActivityScheduleFromDb(
        event.activity_schedule,
        event.is_recurrent ?? false,
      ),
  );
  const [savedContentEvent, setSavedContentEvent] = useState(event);
  const [internalNotes, setInternalNotes] = useState(
    () => event.internal_notes ?? "",
  );

  const patchEventCache = useCallback(
    (updatedEvent: EventEditor) => {
      queryClient.setQueryData<EventEditQueryData>(
        ["event-edit", event.id],
        (current) => {
          if (!current) return current;
          return { ...current, event: updatedEvent };
        },
      );
    },
    [event.id, queryClient],
  );

  const isActivity = eventCategory === "ACTIVITY";

  function handleCategoryChange(category: EventCategoryValue) {
    setEventCategory(category);

    if (category !== "ACTIVITY") {
      setActivityType("");
      setIsFamily(false);
      setIsRecurrent(false);
      setActivitySchedule(emptyPonctuelSchedule());
    }
  }

  function handleRecurrentChange(recurrent: boolean) {
    setIsRecurrent(recurrent);
    setActivitySchedule(
      recurrent ? emptyRecurrentSchedule() : emptyPonctuelSchedule(),
    );
  }

  const savedStatus = resolveEventEditStatus(event);
  const checklistEvent = useMemo(
    () => ({
      cover_image_url:
        (coverImageUploadState.success
          ? coverImageUploadState.data.cover_image_url
          : coverImageState.success
            ? coverImageState.data.cover_image_url
            : event.cover_image_url) ?? null,
      place_id: placeId.trim() || null,
      title,
      description_short: descriptionShort,
      description_long: descriptionLong,
      event_category: eventCategory,
      start_date: startDateLocal
        ? eventCalendarDayToStartIso(startDateLocal)
        : null,
      end_date: endDateLocal ? eventCalendarDayToEndIso(endDateLocal) : null,
    }),
    [
      coverImageUploadState,
      coverImageState,
      event.cover_image_url,
      placeId,
      title,
      descriptionShort,
      descriptionLong,
      eventCategory,
      startDateLocal,
      endDateLocal,
    ],
  );
  const isEditorialChecklistComplete =
    isEventEditorialChecklistComplete(checklistEvent);
  const hasUnsavedContentChange = useMemo(
    () =>
      isEventContentDirty(savedContentEvent, {
        title,
        descriptionShort,
        descriptionLong,
        whartReviewTitle,
        whartReviewContent,
        whartElse,
        startDateLocal,
        endDateLocal,
        eventCategory,
        activityType,
        isFamily,
        isRecurrent,
        pricingInfo,
        activitySchedule,
      }),
    [
      savedContentEvent,
      title,
      descriptionShort,
      descriptionLong,
      whartReviewTitle,
      whartReviewContent,
      whartElse,
      startDateLocal,
      endDateLocal,
      eventCategory,
      activityType,
      isFamily,
      isRecurrent,
      pricingInfo,
      activitySchedule,
    ],
  );
  const contentFieldErrors = contentState.success
    ? undefined
    : contentState.fieldErrors;
  const assignmentFieldErrors = assignmentState.success
    ? undefined
    : assignmentState.fieldErrors;
  const coverImageFieldErrors = coverImageState.success
    ? undefined
    : coverImageState.fieldErrors;
  const isCoverImageBusy = isCoverImagePending || isCoverImageUploadPending;
  const coverImageError = coverImageUploadState.success
    ? undefined
    : coverImageUploadState.error ?? (coverImageState.success ? undefined : coverImageState.error);
  const coverImageSuccess =
    coverImageUploadState.success || coverImageState.success;

  useEffect(() => {
    if (!isStatusPending) {
      setPendingStatusTarget(null);
    }
  }, [isStatusPending]);

  useEffect(() => {
    if (!contentState.success) return;
    const data = contentState.data;
    patchEventCache(data);
    setSavedContentEvent(data);
    setTitle(data.title);
    setDescriptionShort(data.description_short ?? "");
    setDescriptionLong(data.description_long ?? "");
    setWhartReviewTitle(data.whart_review_title ?? "");
    setWhartReviewContent(data.whart_review_content ?? "");
    setWhartElse(data.whart_else ?? "");
    setStartDateLocal(eventIsoToCalendarDay(data.start_date));
    setEndDateLocal(eventIsoToCalendarDay(data.end_date));
    setEventCategory(data.event_category ?? "TEMPORARY_COLLECTION");
    setActivityType(data.activity_type ?? "");
    setIsFamily(data.is_family ?? false);
    setIsRecurrent(data.is_recurrent ?? false);
    setPricingInfo(data.pricing_info ?? "");
    setActivitySchedule(
      parseActivityScheduleFromDb(
        data.activity_schedule,
        data.is_recurrent ?? false,
      ),
    );
  }, [contentState.success, contentState, patchEventCache]);

  useEffect(() => {
    if (!statusState.success) return;
    patchEventCache(statusState.data);
  }, [statusState.success, statusState, patchEventCache]);

  useEffect(() => {
    if (!assignmentState.success) return;
    patchEventCache(assignmentState.data);
    setAssignedTo(assignmentState.data.assigned_to ?? "");
  }, [assignmentState.success, assignmentState, patchEventCache]);

  useEffect(() => {
    if (!coverImageState.success) return;
    patchEventCache(coverImageState.data);
  }, [coverImageState.success, coverImageState, patchEventCache]);

  useEffect(() => {
    if (!coverImageUploadState.success) return;
    patchEventCache(coverImageUploadState.data);
  }, [coverImageUploadState.success, coverImageUploadState, patchEventCache]);

  useEffect(() => {
    if (!placeState.success) return;
    patchEventCache(placeState.data);
    setPlaceId(placeState.data.place_id ?? "");
  }, [placeState.success, placeState, patchEventCache]);

  useEffect(() => {
    if (!internalNotesState.success) return;
    patchEventCache(internalNotesState.data);
    setInternalNotes(internalNotesState.data.internal_notes ?? "");
  }, [internalNotesState.success, internalNotesState, patchEventCache]);

  function submitStatusUpdate(targetStatus: EventEditStatusValue) {
    const formData = new FormData();
    formData.set("id", event.id);
    formData.set("edit_status", targetStatus);
    startTransition(() => {
      statusFormAction(formData);
    });
  }

  function handleStatusAction(targetStatus: EventEditStatusValue) {
    setPendingStatusTarget(targetStatus);
    submitStatusUpdate(targetStatus);
  }

  async function handleDeleteSignal() {
    await deleteEventSignalAction(event.id);
    navigate(getLibraryReturnHref("events"));
  }

  function handleAssigneeChange(value: string) {
    setAssignedTo(value);

    const formData = new FormData();
    formData.set("id", event.id);
    formData.set("assigned_to", value);
    startTransition(() => {
      assignmentAction(formData);
    });
  }

  function handlePlaceChange(value: string) {
    setPlaceId(value);

    const formData = new FormData();
    formData.set("id", event.id);
    formData.set("place_id", value);
    startTransition(() => {
      placeAction(formData);
    });
  }

  function handleInternalNotesBlur() {
    const savedNotes = event.internal_notes ?? "";
    if (internalNotes === savedNotes) return;

    const formData = new FormData();
    formData.set("id", event.id);
    formData.set("internal_notes", internalNotes);
    startTransition(() => {
      internalNotesAction(formData);
    });
  }

  function submitCoverImageRemoval() {
    const formData = new FormData();
    formData.set("id", event.id);
    formData.set("cover_image_url", "");
    startTransition(() => {
      coverImageAction(formData);
    });
  }

  function handleCoverImageUpload(file: File) {
    const formData = new FormData();
    formData.set("id", event.id);
    formData.set("cover_image", file);
    startTransition(() => {
      coverImageUploadAction(formData);
    });
  }

  return (
    <div className="space-y-8 md:grid md:grid-cols-3 md:gap-8 md:space-y-0">
      <form
        action={contentFormAction}
        className="space-y-8 md:col-span-2"
      >
        <input type="hidden" name="id" value={event.id} />

        <div className="flex flex-col items-end gap-2">
          {!contentState.success && contentState.error ? (
            <p
              role="alert"
              className="w-full wireframe-border px-3 py-2 text-sm text-whart-urgent"
            >
              {contentState.error}
            </p>
          ) : null}

          {contentState.success ? (
            <p
              role="status"
              className="w-full wireframe-border px-3 py-2 text-sm text-emerald-700"
            >
              {t("saveContentSuccess")}
            </p>
          ) : null}

          <Button
            type="submit"
            disabled={isContentPending || !hasUnsavedContentChange}
            aria-busy={isContentPending}
            className="inline-flex shrink-0 items-center justify-center gap-2"
          >
            {isContentPending ? <SaveSpinner /> : null}
            {isContentPending ? tCommon("saving") : t("saveContent")}
          </Button>
        </div>

        <EventCoverImageField
          required
          savedUrl={event.cover_image_url}
          disabled={isCoverImageBusy}
          isPending={isCoverImageBusy}
          saveSpinner={<SaveSpinner />}
          fieldError={coverImageFieldErrors?.cover_image_url}
          error={coverImageError}
          success={coverImageSuccess}
          onUpload={handleCoverImageUpload}
          onRemove={submitCoverImageRemoval}
        />

        <section className="space-y-2">
          <FieldLabel htmlFor="event-title" required>
            {t("titleLabel")}
          </FieldLabel>
          <input
            id="event-title"
            name="title"
            type="text"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder={t("titlePlaceholder")}
            disabled={isContentPending}
            className={titleInputClassName}
          />
          <FieldError message={contentFieldErrors?.title} />
        </section>

        <section className="space-y-2">
          <FieldLabel as="p" id="event-category-label">
            {t("categoryLabel")}
          </FieldLabel>
          <input type="hidden" name="event_category" value={eventCategory} />
          <div
            role="radiogroup"
            aria-labelledby="event-category-label"
            className="flex flex-wrap gap-1 rounded-xl wireframe-border bg-zinc-50 p-1"
          >
            {EVENT_CATEGORY_VALUES.map((category) => {
              const isActive = eventCategory === category;

              return (
                <button
                  key={category}
                  type="button"
                  role="radio"
                  disabled={isContentPending}
                  onClick={() => handleCategoryChange(category)}
                  aria-checked={isActive}
                  className={`rounded-xl px-3 py-2 text-sm font-medium transition-colors disabled:opacity-50 ${isActive
                      ? "bg-yellow-100 text-zinc-900 shadow-sm"
                      : "text-neutral-600 hover:bg-white hover:text-zinc-900"
                    }`}
                >
                  {tEventCategories(category)}
                </button>
              );
            })}
          </div>
          <FieldError message={contentFieldErrors?.event_category} />
        </section>

        <section className="space-y-2">
          <FieldLabel htmlFor="event-pricing-info">{t("bookingLinkLabel")}</FieldLabel>
          <input
            id="event-pricing-info"
            name="pricing_info"
            type="url"
            value={pricingInfo}
            disabled={isContentPending}
            onChange={(event) => setPricingInfo(event.target.value)}
            placeholder={t("bookingLinkPlaceholder")}
            className={dateInputClassName}
          />
          <p className="text-xs text-neutral-500">{t("bookingLinkHelp")}</p>
          <FieldError message={contentFieldErrors?.pricing_info} />
        </section>

        {isActivity ? (
          <EventActivityFields
            activityType={activityType}
            isFamily={isFamily}
            isRecurrent={isRecurrent}
            schedule={activitySchedule}
            disabled={isContentPending}
            fieldErrors={contentFieldErrors}
            onActivityTypeChange={setActivityType}
            onIsFamilyChange={setIsFamily}
            onIsRecurrentChange={handleRecurrentChange}
            onScheduleChange={setActivitySchedule}
          />
        ) : null}

        {eventCategory !== "PERMANENT_COLLECTION" ? (
          <section className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <FieldLabel htmlFor="event-start-date" required>
                {t("startDateLabel")}
              </FieldLabel>
              <input
                id="event-start-date"
                name="start_date"
                type="date"
                value={startDateLocal}
                onChange={(event) => setStartDateLocal(event.target.value)}
                disabled={isContentPending}
                className={dateInputClassName}
              />
              <FieldError message={contentFieldErrors?.start_date} />
            </div>
            <div className="space-y-2">
              <FieldLabel htmlFor="event-end-date" required>
                {t("endDateLabel")}
              </FieldLabel>
              <input
                id="event-end-date"
                name="end_date"
                type="date"
                value={endDateLocal}
                onChange={(event) => setEndDateLocal(event.target.value)}
                disabled={isContentPending}
                className={dateInputClassName}
              />
              <FieldError message={contentFieldErrors?.end_date} />
            </div>
          </section>
        ) : null}

        <section className="space-y-4">
          <section className="space-y-2">
            <FieldLabel htmlFor="event-description-short" required>
              {t("abstractLabel")}
            </FieldLabel>
            <textarea
              id="event-description-short"
              name="description_short"
              rows={4}
              value={descriptionShort}
              onChange={(event) => setDescriptionShort(event.target.value)}
              placeholder={t("abstractPlaceholder")}
              disabled={isContentPending}
              className={textareaClassName}
            />
            <FieldError message={contentFieldErrors?.description_short} />
          </section>

          <section className="space-y-2" aria-labelledby="event-content-label">
            <FieldLabel id="event-content-label" as="p">
              {t("contentLabel")}
            </FieldLabel>
            <ArticleRichTextEditor
              editorKey={event.id}
              value={descriptionLong}
              placeholder={t("contentPlaceholder")}
              disabled={isContentPending}
              labels={{
                toolbar: t("editor.toolbar"),
                bold: t("editor.bold"),
                italic: t("editor.italic"),
                underline: t("editor.underline"),
                heading2: t("editor.heading2"),
                heading3: t("editor.heading3"),
                bulletList: t("editor.bulletList"),
                orderedList: t("editor.orderedList"),
                blockquote: t("editor.blockquote"),
              }}
              onChange={setDescriptionLong}
            />
            <input type="hidden" name="description_long" value={descriptionLong} />
            <FieldError message={contentFieldErrors?.description_long} />
          </section>
        </section>

        <section
          className="space-y-6 wireframe-border bg-white p-5"
          aria-labelledby="event-whart-review-section-label"
        >
          <h2
            id="event-whart-review-section-label"
            className="text-base font-semibold text-whart-text"
          >
            {t("whartReviewSectionLabel")}
          </h2>

          <div className="space-y-2">
            <label
              htmlFor="event-whart-review-title"
              className="text-xs font-medium uppercase tracking-wider text-neutral-500"
            >
              {t("whartReviewShortLabel")}
            </label>
            <textarea
              id="event-whart-review-title"
              name="whart_review_title"
              rows={3}
              value={whartReviewTitle}
              onChange={(event) => setWhartReviewTitle(event.target.value)}
              placeholder={t("whartReviewShortPlaceholder")}
              disabled={isContentPending}
              className={textareaClassName}
            />
            <FieldError message={contentFieldErrors?.whart_review_title} />
          </div>

          <div className="space-y-2" aria-labelledby="event-whart-review-content-label">
            <label
              id="event-whart-review-content-label"
              className="text-xs font-medium uppercase tracking-wider text-neutral-500"
            >
              {t("whartReviewLongLabel")}
            </label>
            <ArticleRichTextEditor
              editorKey={`${event.id}-whart-review`}
              value={whartReviewContent}
              placeholder={t("whartReviewLongPlaceholder")}
              disabled={isContentPending}
              labels={{
                toolbar: t("editor.toolbar"),
                bold: t("editor.bold"),
                italic: t("editor.italic"),
                underline: t("editor.underline"),
                heading2: t("editor.heading2"),
                heading3: t("editor.heading3"),
                bulletList: t("editor.bulletList"),
                orderedList: t("editor.orderedList"),
                blockquote: t("editor.blockquote"),
              }}
              onChange={setWhartReviewContent}
            />
            <input
              type="hidden"
              name="whart_review_content"
              value={whartReviewContent}
            />
            <FieldError message={contentFieldErrors?.whart_review_content} />
          </div>

          <div className="space-y-2">
            <label
              htmlFor="event-whart-else"
              className="text-xs font-medium uppercase tracking-wider text-neutral-500"
            >
              {t("whartElseLabel")}
            </label>
            <textarea
              id="event-whart-else"
              name="whart_else"
              rows={4}
              value={whartElse}
              onChange={(event) => setWhartElse(event.target.value)}
              placeholder={t("whartElsePlaceholder")}
              disabled={isContentPending}
              className={textareaClassName}
            />
            <FieldError message={contentFieldErrors?.whart_else} />
          </div>
        </section>
      </form>

      <aside className="mt-8 space-y-4 md:col-span-1 md:mt-0">
        <div className="sticky top-6 space-y-4">
          <EventEditorialChecklist
            event={checklistEvent}
            placeName={currentPlace?.name ?? null}
          />

          <div className="wireframe-border bg-white p-5">
            <EventEditorialCycleCard
              savedStatus={savedStatus}
              itemLabel={event.title}
              isStatusPending={isStatusPending}
              pendingStatusTarget={pendingStatusTarget}
              canPublish={isAdmin}
              canDelete={isAdmin}
              publishDisabled={!isEditorialChecklistComplete}
              onStatusAction={handleStatusAction}
              onDeleteSignal={handleDeleteSignal}
            />
          </div>

          {!statusState.success && statusState.error ? (
            <p
              role="alert"
              className="wireframe-border bg-white px-5 py-3 text-sm text-whart-urgent"
            >
              {statusState.error}
            </p>
          ) : null}

          {statusState.success ? (
            <p
              role="status"
              className="wireframe-border bg-white px-5 py-3 text-sm text-emerald-700"
            >
              {t("saveStatusSuccess")}
            </p>
          ) : null}
        </div>

        <EventAssigneeField
          members={assignableMembers}
          value={assignedTo}
          disabled={isAssignmentPending}
          isPending={isAssignmentPending}
          saveSpinner={<SaveSpinner />}
          fieldError={assignmentFieldErrors?.assigned_to}
          error={assignmentState.success ? undefined : assignmentState.error}
          success={assignmentState.success}
          onChange={handleAssigneeChange}
        />

        <EventPlaceField
          selectedPlace={selectedPlace}
          value={placeId}
          disabled={isPlacePending}
          isPending={isPlacePending}
          saveSpinner={<SaveSpinner />}
          fieldError={placeState.success ? undefined : placeState.fieldErrors?.place_id}
          error={placeState.success ? undefined : placeState.error}
          success={placeState.success}
          onChange={handlePlaceChange}
          onSelectedPlaceChange={setCurrentPlace}
        />

        <EventTaxonomyPanel
          eventId={event.id}
          taxonomy={taxonomy}
          artForms={artForms}
          artisticPeriods={artisticPeriods}
          culturalAreas={culturalAreas}
          exhibitionTypes={exhibitionTypes}
        />

        <ShareLinkButton shareUrl={shareUrl} />

        <InternalNotesField
          entityId={event.id}
          value={internalNotes}
          disabled={isContentPending}
          isPending={isInternalNotesPending}
          saveSpinner={<SaveSpinner />}
          error={
            internalNotesState.success ? undefined : internalNotesState.error
          }
          success={internalNotesState.success}
          onChange={setInternalNotes}
          onBlur={handleInternalNotesBlur}
        />
      </aside>
    </div>
  );
}
